import ErrorUi from 'client/components/core/Error/ui/ErrorUi'
import React, { ReactNode } from 'react'
import Select, {
  components,
  DropdownIndicatorProps,
  GroupBase,
  MultiValueProps,
  MultiValue as MultiValueType,
  OnChangeValue,
  OptionProps,
  SingleValue,
  StylesConfig,
} from 'react-select'
import { SelectComponents } from 'react-select/dist/declarations/src/components'
import { OptionWrapper } from 'src/editor/layout/sidebar/settings/option-wrapper'
import { ChevronUpIcon } from 'src/editor/shared/icons/chevron-up-icon'
import { Badge } from 'src/editor/shared/ui-lib/badge'
import {
  BadgeColorEnum,
  BadgeSizeEnum,
} from 'src/editor/shared/ui-lib/shared/badge-consts'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { twMerge } from 'tailwind-merge'
import { Loader } from '../loader'

export type OptionValue = string | number | null | boolean | undefined

export type OptionType<Value extends OptionValue = string> = {
  label: string
  value: Value
  nonRemovable?: boolean
  [key: string]: unknown
}

export type OptionSingleValueType<
  Value extends OptionValue = string,
  Option extends OptionType<Value> = {
    label: string
    value: Value
  },
> = SingleValue<Option>

export const Option = <
  Value extends OptionValue,
  IsMulti extends boolean,
  Option extends OptionType<Value>,
>(
  props: OptionProps<Option, IsMulti>,
) => {
  const { label } = props
  return <components.Option {...props}>{label}</components.Option>
}

const DropdownIndicator = <
  Value extends OptionValue,
  IsMulti extends boolean,
  Option extends OptionType<Value>,
>(
  props: DropdownIndicatorProps<Option, IsMulti>,
) => {
  const { menuIsOpen, isDisabled } = props.selectProps
  return (
    <components.DropdownIndicator {...props} className="mr-1">
      <ChevronUpIcon
        className={twMerge(
          'h-4 w-4 transition-transform duration-150',
          menuIsOpen ? 'rotate-0' : 'rotate-180',
          isDisabled
            ? '[&>path]:stroke-iconDisabled'
            : '[&>path]:stroke-iconTertiary',
        )}
      />
    </components.DropdownIndicator>
  )
}

const MultiValue = <
  Value extends OptionValue,
  IsMulti extends boolean,
  Option extends OptionType<Value>,
>(
  props: MultiValueProps<Option, IsMulti>,
) => {
  return (
    <Badge
      color={BadgeColorEnum.Gray}
      size={BadgeSizeEnum.Small}
      closeButtonProps={{
        ...(props.removeProps as React.HTMLAttributes<SVGSVGElement>),
      }}
      withoutCloseButton={
        props.selectProps.isDisabled || props.data.nonRemovable
      }
    >
      {props.data.label}
    </Badge>
  )
}

interface BaseProps<
  Value extends OptionValue,
  Option extends OptionType<Value>,
> {
  size?: 'medium' | 'large' | 'small'
  labelText?: string
  required?: boolean
  labelIcon?: ReactNode
  helpMessage?: string
  skipTranslation?: boolean
  placeholder?: string
  withoutWrapper?: boolean
  styles?: StylesConfig<Option, boolean> | undefined
  itemStyles?: React.CSSProperties
  menuPortalTarget?: HTMLElement | null
  isLoading?: boolean
  isDisabled?: boolean
  error?: string
  onOpen?: () => void
  isClearable?: boolean
  isSearchable?: boolean
  className?: string
  wrapperClassName?: string
}

export type ReactSelectProps<
  Value extends OptionValue = string,
  IsMulti extends boolean = false,
  Option extends OptionType<Value> = OptionType<Value>,
> = BaseProps<Value, Option> & {
  isMulti?: IsMulti
  options: Option[]
  selectedOption: IsMulti extends true ? MultiValueType<Option> : Option | null
  onChange: (option: OnChangeValue<Option, IsMulti>) => void
  components?: Partial<SelectComponents<Option, IsMulti, GroupBase<Option>>>
}

export const SelectNew = <
  Value extends OptionValue,
  IsMulti extends boolean = false,
  Option extends OptionType<Value> = OptionType<Value>,
>({
  size = 'medium',
  selectedOption,
  onChange,
  labelText,
  required,
  labelIcon,
  helpMessage,
  skipTranslation = false,
  placeholder,
  options,
  isMulti,
  withoutWrapper = false,
  styles,
  menuPortalTarget,
  itemStyles,
  isLoading = false,
  isDisabled = false,
  isClearable = false,
  isSearchable,
  className,
  error,
  onOpen,
  components,
  wrapperClassName,
}: ReactSelectProps<Value, IsMulti, Option>) => {
  const { t } = useTypedTranslation()
  const select = (
    <Select<Option, IsMulti>
      placeholder={placeholder}
      onChange={onChange}
      value={selectedOption}
      options={options}
      noOptionsMessage={() => (
        <span className="leading-10">
          {t('components.select.no_options_message')}
        </span>
      )}
      loadingMessage={() => (
        <div className="flex h-10 w-full items-center justify-center">
          <Loader />
        </div>
      )}
      isMulti={isMulti}
      isDisabled={isDisabled}
      menuPortalTarget={menuPortalTarget}
      components={{
        DropdownIndicator,
        MultiValue,
        Option,
        ...components,
      }}
      isClearable={isClearable}
      isSearchable={isSearchable}
      unstyled
      classNames={{
        control: ({ menuIsOpen, isDisabled, isFocused }) =>
          twMerge(
            'cursor-default rounded-[6px] border-[1px] border-solid p-2.5',
            'relative flex items-center justify-between gap-1',
            'bg-white outline-borderInteractive/20 transition-all duration-100',
            isDisabled
              ? '!bg-bgPrimaryBase border-inputBorderDisabled'
              : menuIsOpen
                ? 'border-inputBorderInteractive'
                : 'border-inputBorder hover:border-inputBorderHover',
            isFocused && 'border-inputBorderInteractive outline-4',
            size === 'large' && 'min-h-[50px] text-L',
            size === 'medium' && 'min-h-[32px] text-M',
            size === 'small' && 'min-h-[24px] px-3 py-2 text-M',
          ),
        container: () => 'position: relative',
        valueContainer: () => 'flex flex-wrap gap-1',
        singleValue: ({ isDisabled }) =>
          isDisabled ? 'text-textDisabled' : 'text-textPrimary',
        multiValue: () =>
          'inline-flex items-center bg-gray-50 mr-1 py-0.5 px-2 rounded-sm',
        multiValueRemove: () => 'ml-1.5 cursor-pointer',
        placeholder: ({ isDisabled }) =>
          isDisabled ? 'text-textDisabled' : 'text-textTertiary',
        menu: () =>
          twMerge(
            'user-select-none overflow-hidden',
            'mt-2 min-h-10 rounded-[6px] border border-solid',
            'border-inputBorder bg-inputBasePrimary',
          ),
        option: ({ isSelected }) =>
          twMerge(
            'hover:!bg-bgSecondary !cursor-pointer p-2.5 text-M',
            isSelected ? '!bg-bgSecondary' : '!bg-bgPrimaryBase',
          ),
      }}
      styles={{
        ...styles,
        container: () => ({}),
        valueContainer: () => ({}),
        control: () => ({}),
        singleValue: base => ({
          ...base,
          whiteSpace: 'normal',
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 }),
      }}
      onMenuOpen={() => {
        if (onOpen) {
          onOpen()
        }
      }}
      isLoading={isLoading}
      className={className}
    />
  )

  if (withoutWrapper) {
    return select
  } else {
    return (
      <OptionWrapper
        helpMessage={helpMessage}
        labelText={labelText}
        required={required}
        labelIcon={labelIcon}
        itemStyles={itemStyles}
        skipTranslation={skipTranslation}
        className={wrapperClassName}
      >
        {select}
        {error ? <ErrorUi dangerouslySetInnerHTML={{ __html: error }} /> : null}
      </OptionWrapper>
    )
  }
}
